import { useState, useCallback } from 'react';
import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';

/**
 * Custom hook to handle all protocol-related actions
 * Centralizes voting, activation, copying, and data fetching logic
 */
export const useProtocolActions = () => {
  const [userVotes, setUserVotes] = useState({});
  const [votingProtocolId, setVotingProtocolId] = useState(null);
  const [likedProtocols, setLikedProtocols] = useState([]);
  const [likingProtocolId, setLikingProtocolId] = useState(null);

  // Combined fetch for user votes and liked protocols to avoid duplicate queries
  const fetchUserProtocolRatings = useCallback(async () => {
    // Use deduplication to prevent multiple simultaneous requests
    const cacheKey = getCacheKey('fetchUserProtocolRatings', 'all');
    
    return dedupeRequest(cacheKey, async () => {
      try {
        // Use getSession() to avoid network requests
        const { data: { session } = {} } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) {
          setUserVotes({});
          setLikedProtocols([]);
          return { votes: {}, likedProtocols: [] };
        }

        // Single query to get all ratings data at once
        const { data: ratings, error } = await supabase
          .from('protocol_ratings')
          .select('protocol_id, rating')
          .eq('user_id', user.id);

        if (error) throw error;

        // Build a map of protocol_id -> vote value (1 for upvote, -1 for downvote)
        const votesMap = {};
        const likedIds = [];
        
        (ratings || []).forEach(rating => {
          votesMap[rating.protocol_id] = rating.rating;
          // Include all rated protocols in liked list (for backwards compatibility)
          likedIds.push(rating.protocol_id);
        });
        
        setUserVotes(votesMap);
        setLikedProtocols(likedIds);
        return { votes: votesMap, likedProtocols: likedIds };
      } catch (error) {
        console.error('Error fetching user protocol ratings:', error);
        setUserVotes({});
        setLikedProtocols([]);
        return { votes: {}, likedProtocols: [] };
      }
    });
  }, []);

  // Fetch user's votes (upvotes/downvotes) - now uses combined fetch
  const fetchUserVotes = useCallback(async () => {
    const result = await fetchUserProtocolRatings();
    return result.votes;
  }, [fetchUserProtocolRatings]);

  // Fetch user's liked protocols (for backwards compatibility) - now uses combined fetch
  const fetchLikedProtocols = useCallback(async () => {
    const result = await fetchUserProtocolRatings();
    return result.likedProtocols;
  }, [fetchUserProtocolRatings]);

  // Handle upvote/downvote protocol
  const handleVote = useCallback(async (protocol, voteType, onSuccess, onError) => {
    try {
      // Use getSession() to avoid network requests
      const { data: { session } = {} } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        if (onError) onError('Please sign in to vote on protocols');
        return;
      }

      setVotingProtocolId(protocol.id);
      const currentVote = userVotes[protocol.id];
      
      // Determine the new vote: if clicking same vote, remove it; otherwise, set new vote
      let newVote = null;
      if (currentVote === voteType) {
        // User is removing their vote
        newVote = null;
      } else {
        // User is changing their vote or voting for the first time
        newVote = voteType;
      }

      // Calculate new counts based on vote change
      const oldUpvotes = protocol.upvotes_count || 0;
      const oldDownvotes = protocol.downvotes_count || 0;
      let newUpvotes = oldUpvotes;
      let newDownvotes = oldDownvotes;

      if (currentVote === 1) {
        newUpvotes -= 1;
      } else if (currentVote === -1) {
        newDownvotes -= 1;
      }

      if (newVote === 1) {
        newUpvotes += 1;
      } else if (newVote === -1) {
        newDownvotes += 1;
      }

      // Update UI optimistically
      setUserVotes(prev => {
        const next = { ...prev };
        if (newVote === null) {
          delete next[protocol.id];
        } else {
          next[protocol.id] = newVote;
        }
        return next;
      });

      // Update database
      if (newVote === null) {
        // Delete the vote
        const { error } = await supabase
          .from('protocol_ratings')
          .delete()
          .eq('protocol_id', protocol.id)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Use upsert to atomically insert or update the vote
        // This prevents race conditions where multiple requests try to insert simultaneously
        const { error: upsertError } = await supabase
          .from('protocol_ratings')
          .upsert({
            protocol_id: protocol.id,
            user_id: user.id,
            rating: newVote
          }, { 
            onConflict: 'user_id,protocol_id' 
          });

        if (upsertError) throw upsertError;
      }

      // Refresh data in background
      await fetchUserVotes();
      
      // Call success callback with updated counts
      if (onSuccess) {
        onSuccess({
          protocol_id: protocol.id,
          upvotes_count: newUpvotes,
          downvotes_count: newDownvotes
        });
      }
    } catch (error) {
      console.error('Error voting on protocol:', error);
      if (onError) onError('Error updating vote. Please try again.');
      
      // Revert optimistic updates
      await fetchUserVotes();
    } finally {
      setVotingProtocolId(null);
    }
  }, [userVotes, fetchUserVotes]);

  // Handle protocol like/unlike (backwards compatibility)
  const handleToggleLike = useCallback(async (protocol, onSuccess, onError) => {
    try {
      // Use getSession() to avoid network requests
      const { data: { session } = {} } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        if (onError) onError('Please sign in to like protocols');
        return;
      }

      setLikingProtocolId(protocol.id);
      const isCurrentlyLiked = likedProtocols.includes(protocol.id);

      if (isCurrentlyLiked) {
        // Unlike protocol
        const { error } = await supabase
          .from('protocol_ratings')
          .delete()
          .eq('protocol_id', protocol.id)
          .eq('user_id', user.id);

        if (error) throw error;

        // Update local state
        setLikedProtocols(prev => prev.filter(id => id !== protocol.id));
        
        if (onSuccess) {
          onSuccess({
            protocol_id: protocol.id,
            likes_count: Math.max(0, (protocol.likes_count || 0) - 1),
            isLiked: false
          });
        }
      } else {
        // Like protocol
        const { error } = await supabase
          .from('protocol_ratings')
          .insert({
            protocol_id: protocol.id,
            user_id: user.id,
            rating: 5 // Default rating of 5 for "liked"
          });

        if (error) throw error;

        // Update local state
        setLikedProtocols(prev => [...prev, protocol.id]);
        
        if (onSuccess) {
          onSuccess({
            protocol_id: protocol.id,
            likes_count: (protocol.likes_count || 0) + 1,
            isLiked: true
          });
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      if (onError) onError('Error updating like. Please try again.');
    } finally {
      setLikingProtocolId(null);
    }
  }, [likedProtocols]);

  return {
    // State
    userVotes,
    votingProtocolId,
    likedProtocols,
    likingProtocolId,
    
    // Actions
    handleVote,
    handleToggleLike,
    fetchUserVotes,
    fetchLikedProtocols
  };
};

